<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">订单管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">售后</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">售后单</span></h2>
      <div class="header-actions">
        <el-select v-model="filterStatus" placeholder="筛选状态" clearable style="width:140px" @change="fetchData">
          <el-option label="待审核" value="pending" />
          <el-option label="已通过" value="approved" />
          <el-option label="已驳回" value="rejected" />
        </el-select>
        <el-input v-model="searchKeyword" placeholder="搜索订单号" clearable prefix-icon="Search" style="width:200px" @input="fetchData" />
      </div>
    </div>

    <el-table :data="list" stripe v-loading="loading">
      <el-table-column label="售后编号" prop="refund_no" width="170" />
      <el-table-column label="商品" min-width="140">
        <template #default="{row}">{{row.order_item?.product_name||row.orderItem?.product_name||'-'}}</template>
      </el-table-column>
      <el-table-column label="数量" width="55" prop="quantity" />
      <el-table-column label="退款金额" width="100">
        <template #default="{row}">¥{{row.refund_amount}}</template>
      </el-table-column>
      <el-table-column label="原因" min-width="140" show-overflow-tooltip prop="reason" />
      <el-table-column label="凭证" width="100">
        <template #default="{row}">
          <el-image v-if="row.images?.length" :src="row.images[0]" style="width:36px;height:36px;border-radius:4px;cursor:pointer" fit="cover" :preview-src-list="row.images" preview-teleported />
          <span v-else style="color:#ccc">-</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="80">
        <template #default="{row}">
          <el-tag :type="statusType(row.status)" size="small">{{statusLabel(row.status)}}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="申请时间" width="140">
        <template #default="{row}">{{formatDateTime(row.created_at)}}</template>
      </el-table-column>
      <el-table-column label="处理人" width="90">
        <template #default="{row}">{{row.processor?.name||'-'}}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{row}">
          <template v-if="row.status==='pending'">
            <el-button size="small" type="success" @click="handleApprove(row)">通过</el-button>
            <el-button size="small" type="warning" @click="handleReject(row)">驳回</el-button>
          </template>
          <span v-else style="color:#909399">已处理</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 审核弹窗 -->
    <el-dialog v-model="approveDialog" title="审核通过" width="400px">
      <el-form label-width="100px">
        <el-form-item label="申请金额">¥{{approveTarget?.refund_amount}}</el-form-item>
        <el-form-item label="调整金额">
          <el-input-number v-model="approveAmount" :precision="2" :min="0.01" :max="approveTarget?.refund_amount" style="width:200px" />
          <div class="tip">不超过原申请金额，留空按原金额退款</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="approveDialog=false">取消</el-button>
        <el-button type="primary" @click="confirmApprove" :loading="approving">确认通过</el-button>
      </template>
    </el-dialog>

    <div class="pagination-wrap" v-if="pagination.total>0">
      <el-pagination
        v-model:current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :total="pagination.total"
        layout="total, prev, pager, next"
        @current-change="onPageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as api from '../../api/orders';

const list = ref([]);
const loading = ref(false);
const filterStatus = ref('');
const searchKeyword = ref('');
const pagination = ref({ current_page: 1, per_page: 20, total: 0 });
const approveDialog = ref(false);
const approveTarget = ref(null);
const approveAmount = ref(null);
const approving = ref(false);

const statusLabel = (s) => ({ pending:'待审核', approved:'已通过', rejected:'已驳回' }[s] || s);
const statusType = (s) => ({ pending:'warning', approved:'success', rejected:'danger' }[s] || 'info');
const formatDateTime = (v) => { if (!v) return '-'; const d = new Date(v); if (isNaN(d.getTime())) return v; return d.toLocaleString('zh-CN', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' }); };

const fetchData = async () => {
  loading.value = true;
  try {
    const params = { page: pagination.value.current_page, per_page: pagination.value.per_page };
    if (filterStatus.value) params.status = filterStatus.value;
    if (searchKeyword.value) params.search = searchKeyword.value;
    const r = await api.getAfterSaleList(params);
    const d = r.data || {};
    list.value = d.data || [];
    pagination.value = d.pagination || d || { current_page: 1, per_page: 20, total: 0 };
  } catch {} finally { loading.value = false; }
};

const onPageChange = (page) => { pagination.value.current_page = page; fetchData(); };

const handleApprove = (row) => {
  approveTarget.value = row;
  approveAmount.value = null;
  approveDialog.value = true;
};

const confirmApprove = async () => {
  approving.value = true;
  try {
    const params = {};
    if (approveAmount.value !== null && approveAmount.value > 0) {
      params.amount = approveAmount.value;
    }
    await api.approveAfterSale(approveTarget.value.id, params);
    ElMessage.success('已审核通过，已创建退款单');
    approveDialog.value = false;
    await fetchData();
  } catch (e) { ElMessage.error(e.response?.data?.message || '操作失败'); }
  finally { approving.value = false; }
};

const handleReject = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入驳回原因', '驳回售后', { confirmButtonText:'确认驳回', cancelButtonText:'取消', inputType:'textarea' });
    await api.rejectAfterSale(row.id, { remark: value || '驳回' });
    ElMessage.success('已驳回');
    await fetchData();
  } catch { }
};

onMounted(fetchData);
</script>

<style scoped>
.header-actions { display: flex; gap: 12px; align-items: center; }
.pagination-wrap { display: flex; justify-content: flex-end; margin-top: 16px; }
.tip { font-size: 12px; color: #999; margin-top: 4px; }
</style>
